// "Aktarılmayan satırlar" popover'ı — Geçmiş Rapor Aktarımı, P6 (2026-09-30).
//
// Kısmi kabul edilen bir yanıtta ("3/5 satır aktarıldı") kullanıcı "aktarılmayan
// satırları gör" der; burada düşen her satır/parça, red sebebi ve modelin önerdiği
// ham satır okunur biçimde listelenir. SALT OKUNUR — bu sürümde "bu değeri kullan"
// YOK (satır bazlı kısmi kabulün yazma yolu ayrı bir iş, TODO-IMPORT-09'un devamı).
//
// Kabuk `RejectedProposalPopover` ikizi: role=dialog + aria-modal + Esc + odak tuzağı
// + kapanışta odağın tetikleyiciye dönmesi.

import { useEffect, useId, useRef } from "react";
import { readCompositeParts, type PartialAcceptance, type QuestionSnapshot } from "@mahir/shared";
import { fillTemplate } from "../../lib/period-ui";
import { IMPORT_COPY, PARTIAL_ROWS_VISIBLE } from "../../lib/report-import-ui";

export interface PartialRowsPopoverProps {
  open: boolean;
  partial: PartialAcceptance;
  /** Sütun/alt alan/parça ETİKETLERİNİ çözmek için tam soru (inceleme C5). */
  question: QuestionSnapshot;
  onClose: () => void;
}

/**
 * C5 (inceleme) — hücre anahtarı → insan okunur etiket. LLM satırı `{span, cells}`
 * ham payload'ıdır (bir `FormValue` DEĞİL), o yüzden `formatFormValue` kullanılamaz;
 * etiketler soru tanımından tek tek çözülür. Tablo sütunu / repeater alt alanı /
 * composite parçası — üçü de aranır, bulunamazsa ham kimlik kalır.
 */
function buildLabelMap(question: QuestionSnapshot): Map<string, string> {
  const map = new Map<string, string>();
  const options = question.options as Record<string, unknown> | null;
  const columns = options?.columns;
  if (Array.isArray(columns)) {
    for (const c of columns) {
      const col = c as { id?: unknown; label?: unknown };
      if (typeof col.id === "string" && typeof col.label === "string") map.set(col.id, col.label);
    }
  }
  const subFields = options?.subFields;
  if (Array.isArray(subFields)) {
    for (const f of subFields) {
      const sub = f as { id?: unknown; label?: unknown };
      if (typeof sub.id === "string" && typeof sub.label === "string") map.set(sub.id, sub.label);
    }
  }
  for (const part of readCompositeParts(question.options) ?? []) {
    map.set(part.key, part.label);
    const partOptions = part.options as unknown as Record<string, unknown>;
    for (const key of ["columns", "subFields"] as const) {
      const list = partOptions[key];
      if (!Array.isArray(list)) continue;
      for (const item of list) {
        const f = item as { id?: unknown; label?: unknown };
        if (typeof f.id === "string" && typeof f.label === "string") map.set(f.id, f.label);
      }
    }
  }
  return map;
}

/** "tablo · satır 2" gibi bileşik indekste parça kodunu etikete çevirir. */
function labelIndex(index: string, labels: Map<string, string>): string {
  return index
    .split(" · ")
    .map((part) => labels.get(part) ?? part)
    .join(" · ");
}

/** Skaler hücre değeri — nesne/dizi gelirse JSON'a düşer ("[object Object]" olmaz). */
function cellText(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return value.toString();
  try {
    return JSON.stringify(value) ?? "—";
  } catch {
    return "—";
  }
}

/** Önerilen satırı "etiket: değer" satırlarına indirger. */
function formatProposedRow(proposed: unknown, labels: Map<string, string>): string {
  if (proposed === null || proposed === undefined) return "—";
  if (typeof proposed !== "object") return cellText(proposed);
  const record = proposed as Record<string, unknown>;
  // Ham satır `{ span, cells }` şeklinde gelebilir — hücreler asıl ilgi çeken kısım.
  const cells = record.cells;
  const source = cells !== null && typeof cells === "object" ? (cells as typeof record) : record;
  const parts = Object.entries(source)
    .filter(([key]) => key !== "span")
    .map(([key, value]) => `${labels.get(key) ?? key}: ${cellText(value)}`);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

export function PartialRowsPopover(props: PartialRowsPopoverProps): JSX.Element | null {
  const { open, partial, question, onClose } = props;
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Effect deps YALNIZ [open] — `onClose` her render'da yeni referans (bkz. ikiz bileşen).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      returnFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const shown = partial.rejectedRows.slice(0, PARTIAL_ROWS_VISIBLE);
  const hidden = partial.rejectedRows.length - shown.length;
  const labels = buildLabelMap(question);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="rounded-xl p-5 max-w-lg w-full mx-4"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <h2
          id={titleId}
          style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem", marginBottom: 4 }}
        >
          {IMPORT_COPY.partialTitle}
        </h2>
        {/* 15A — 14 px altı metin her yerde --color-text-2. */}
        <p style={{ fontSize: "0.78rem", color: "var(--color-text-2)", marginBottom: 12 }}>
          {question.code} ·{" "}
          {fillTemplate(IMPORT_COPY.partialSummary, {
            accepted: partial.acceptedRows,
            total: partial.totalRows,
          })}
        </p>

        <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: 280, overflowY: "auto" }}>
          {shown.map((row, i) => (
            <li
              key={`${row.index}-${i.toString()}`}
              style={{
                borderLeft: "3px solid var(--color-warning-border)",
                paddingLeft: 8,
                marginBottom: 10,
              }}
            >
              <p
                style={{ fontSize: "0.78rem", color: "var(--color-warning-text)", fontWeight: 600 }}
              >
                {labelIndex(row.index, labels)} ·{" "}
                {row.reason === "schema"
                  ? IMPORT_COPY.rejectedReasonSchema
                  : IMPORT_COPY.rejectedReasonGrounding}
              </p>
              <p
                style={{
                  fontSize: "0.82rem",
                  color: "var(--color-text)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {formatProposedRow(row.proposed, labels)}
              </p>
            </li>
          ))}
        </ul>
        {hidden > 0 ? (
          <p style={{ fontSize: "0.78rem", color: "var(--color-text-2)" }}>
            {fillTemplate(IMPORT_COPY.partialRowsTruncated, { n: hidden })}
          </p>
        ) : null}

        <div className="flex justify-end gap-2" style={{ marginTop: 16 }}>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            {IMPORT_COPY.proposalClose}
          </button>
        </div>
      </div>
    </div>
  );
}
